import React, { useEffect, useMemo, useState } from "react";
import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer, NodeViewWrapper } from "@tiptap/react";
import * as FiIcons from "react-icons/fi";
import SafeIcon from "../../../common/SafeIcon.jsx";
import MusicNoteGame from "./noteReadingGame/MusicNoteGame.jsx";
import ConfirmModal from "../../common/ConfirmModal.jsx";

const { FiMusic, FiTrash2 } = FiIcons;
const NOTE_KEY_EVENT = "mms:note-reading-key";

const isFormControl = (el) =>
  el &&
  (el.tagName === "SELECT" ||
    el.tagName === "INPUT" ||
    el.tagName === "TEXTAREA" ||
    el.isContentEditable);

function isPlainLetterAG(e) {
  if (!e || e.isComposing || e.ctrlKey || e.metaKey || e.altKey) return null;
  const k = (e.key || "").toUpperCase();
  return /^[A-G]$/.test(k) ? k : null;
}

function NoteReadingGameBlock({ node, updateAttributes, editor, deleteNode }) {
  const attrs = node.attrs || {};
  const [isPhone, setIsPhone] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const ctx = editor?.storage?.mms ?? {};
  const canDelete = !!ctx?.userId && !!editor?.isEditable; // teachers only

  // Ensure stable block id once
  useEffect(() => {
    if (!attrs.noteBlockId) {
      updateAttributes({ noteBlockId: crypto.randomUUID() });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Phone sizing (keep it simple: <640px treated as phone)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 639px)");
    const apply = () => setIsPhone(!!mq.matches);
    apply();
    mq.addEventListener?.("change", apply);
    return () => mq.removeEventListener?.("change", apply);
  }, []);

  const handleDelete = async () => {
    // purely a document node: no DB instance to delete
    setDeleting(true);
    try {
      deleteNode?.();
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <NodeViewWrapper
      as="div"
      contentEditable={false}
      role="group"
      tabIndex={-1}
      className="relative group w-full rounded-2xl bg-slate-50 shadow-sm p-4 border border-slate-200"
      // Prevent ProseMirror from hijacking interaction
      onMouseDownCapture={(e) => {
        if (!isFormControl(e.target)) e.stopPropagation();
      }}
      onTouchStartCapture={(e) => {
        if (!isFormControl(e.target)) e.stopPropagation();
      }}
      // Capture A–G before TipTap and forward to the game
      onKeyDownCapture={(e) => {
        // only handle when the editor is focused (prevents keyboard trap)
        if (!editor?.isFocused) return;
        const letter = isPlainLetterAG(e);
        if (!letter) return;
        e.preventDefault();
        e.stopPropagation();
        window.dispatchEvent(
          new CustomEvent(NOTE_KEY_EVENT, { detail: { letter } })
        );
      }}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 bg-orange-100 text-orange-600 rounded-xl flex items-center justify-center">
            <SafeIcon icon={FiMusic} className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="text-base font-bold text-slate-800">
              Note Reading Game
            </div>
            <div className="text-xs text-slate-500">
              Identify the note on the staff.
            </div>
          </div>
        </div>

        {canDelete && (
          <button
            className="p-2 rounded-full text-slate-500 hover:bg-red-100 hover:text-red-600 transition-colors opacity-50 group-hover:opacity-100"
            disabled={deleting}
            onClick={() => setShowDeleteConfirm(true)}
            title="Delete Tool"
          >
            <SafeIcon icon={FiTrash2} className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Game surface */}
      <div
        className="bg-white rounded-xl border border-slate-200 overflow-hidden outline-none focus:ring-2 focus:ring-orange-300"
        aria-label="Note Reading Game"
      >
        {/* On phones we want the "big" layout */}
        <MusicNoteGame compact={!isPhone} />
      </div>

      {showDeleteConfirm && (
        <ConfirmModal
          title="Delete Note Reading Game?"
          message="This will remove the tool from the lesson note."
          confirmText={deleting ? "Deleting..." : "Delete"}
          confirmButtonClass="bg-red-500 hover:bg-red-600"
          confirmDisabled={deleting}
          onConfirm={handleDelete}
          onClose={() => setShowDeleteConfirm(false)}
        />
      )}
    </NodeViewWrapper>
  );
}

const NoteReadingGameNode = Node.create({
  name: "noteReadingGame",
  group: "block",
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      noteBlockId: { default: null },
      lessonNoteId: { default: null },
      studentId: { default: null },
      studentMasterId: { default: null },
    };
  },

  parseHTML() {
    return [{ tag: "note-reading-game" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["note-reading-game", mergeAttributes(HTMLAttributes)];
  },

  addNodeView() {
    return ReactNodeViewRenderer(NoteReadingGameBlock);
  },
});

export default NoteReadingGameNode;
export { NoteReadingGameNode };